import {
  AutocompleteInput,
  Button,
  Form,
  Grid,
  InputGroup,
  SelectInput,
  SelectOption,
  Stack,
  TextInput,
} from '@garner-health/components-common';
import { SR, useStringResource } from '@garner-health/lib-ui-content-management';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import React, { useCallback, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import { analytics, Event } from '~/analytics';
import { Network } from '~/clients';
import { useNetworks } from '~/contexts';

import {
  CareGoalSelectOption,
  getCareGoalsSelectOptions,
  getGeocodeLocationSelectOptions,
  LocationSelectOption,
} from './common';
import {
  Gender,
  languageOptions,
  ProviderTypeFilter,
  useGenderOptions,
  useProviderTypeOptions,
} from './select-options';

type NetworkSelectOption = SelectOption & {
  isHcscNetwork: boolean;
};

export type SearchFormData = {
  careGoal: CareGoalSelectOption | null;
  providerName: string | null;
  location: LocationSelectOption;
  network: NetworkSelectOption;
  language: string | null;
  gender: Gender | '' | null;
  providerType: ProviderTypeFilter | '' | null;
};

export type UnvalidatedSearchFormData = { [K in keyof SearchFormData]: SearchFormData[K] | null };

type SearchFormProps = {
  initialValues: SearchFormData | null;
  isDisabled: boolean;
  onSubmit: (data: SearchFormData) => void;
  shouldReset: boolean;
};

export function fetchNetworkData(networks: Network[], input: string): Promise<NetworkSelectOption[]> {
  const filteredNetworks = input
    ? networks.filter(network => network.name.toLowerCase().includes(input.toLowerCase()))
    : networks;

  return Promise.resolve(
    filteredNetworks.map(({ name, id, isHcscNetwork }) => ({
      label: name,
      value: id,
      isHcscNetwork,
    })),
  );
}

function trackSearch(formData: UnvalidatedSearchFormData) {
  // Name and Care Goal
  if (formData.providerName && formData.careGoal) {
    analytics.track(Event.CARE_GOAL_AND_NAME_SEARCH, {
      careGoal: formData.careGoal.label,
      careGoalType: formData.careGoal.type,
      providerName: formData.providerName,
    });
  }
  // Name only
  if (formData.providerName) {
    analytics.track(Event.NAME_SEARCH, { providerName: formData.providerName });
  }
  // Care goal only
  else {
    analytics.track(Event.CARE_GOAL_SEARCH, {
      careGoal: formData.careGoal!.label,
      careGoalType: formData.careGoal!.type,
    });
  }
  const specialty = formData.careGoal?.specialty ?? null;

  if (formData.language) analytics.track(Event.FILTER_LANGUAGE, { specialty, language: formData.language });
  if (formData.gender) analytics.track(Event.FILTER_GENDER, { specialty, gender: formData.gender });
  if (formData.providerType) {
    analytics.track(Event.FILTER_PROVIDER_TYPE, { specialty, providerType: formData.providerType });
  }
}

//TODO: improve object comparison: https://app.asana.com/0/1203374383582495/1205161221217593/f
export function hasSameValues(formData1: UnvalidatedSearchFormData, formData2: UnvalidatedSearchFormData) {
  if (formData1.network?.value !== formData2.network?.value) return false;
  if (formData1.careGoal?.value !== formData2.careGoal?.value) return false;
  if (formData1.location?.value !== formData2.location?.value) return false;
  if (formData1.location?.label !== formData2.location?.label) return false;
  if (formData1.providerName !== formData2.providerName) return false;
  if (formData1.language !== formData2.language) return false;
  if (formData1.gender !== formData2.gender) return false;
  if (formData1.providerType !== formData2.providerType) return false;
  return true;
}

/**
 * Validates that at least one of careGoal and providerName is present on the form
 */
function specialtyOrNameValidator(message: string) {
  return {
    validation: {
      validator: (_: unknown, formValues: FieldValues) => {
        return !!(formValues.careGoal || formValues.providerName?.trim());
      },
      renderMessage: () => message,
    },
  };
}

export const SearchForm = ({ initialValues, isDisabled, onSubmit, shouldReset }: SearchFormProps) => {
  const form = useForm<UnvalidatedSearchFormData>();
  const genderOptions = useGenderOptions();
  const providerTypeOptions = useProviderTypeOptions();
  const networks = useNetworks();

  const careGoalPlaceholder = useStringResource('search', 'careGoalPlaceholderText');
  const locationPlaceholder = useStringResource('search', 'locationPlaceholderText');
  const providerNamePlaceholder = useStringResource('search', 'providerNamePlaceholderText');
  const networkPlaceholder = useStringResource('search', 'networkPlaceholderText');
  const languagePlaceholder = useStringResource('search', 'languagePlaceholderText');
  const genderPlaceholder = useStringResource('search', 'genderPlaceholderText');
  const providerTypePlaceholder = useStringResource('search', 'providerTypePlaceholderText');

  const careGoalExampleText = useStringResource('search', 'careGoalExampleText');
  const locationExampleText = useStringResource('search', 'locationExampleText');
  const networkExampleText = useStringResource('search', 'networkExampleText');

  const specialtyOrNameValidationText = useStringResource('search', 'specialtyOrNameValidationText');
  const isNarrowView = innerWidth < 900;

  useEffect(() => {
    if (!initialValues) return;
    // the form could already have the initial values because the form remounts when it submits and updates the url params
    if (hasSameValues(form.getValues(), initialValues)) return;
    form.reset(initialValues);
  }, []);

  useEffect(() => {
    if (!shouldReset) return;
    form.reset({
      careGoal: null,
      providerName: null,
      location: null,
      network: null,
      language: null,
      gender: null,
      providerType: null,
    });
  }, [shouldReset]);

  // `watch`, not `useWatch`: only `watch` re-reads after the URL-driven `form.reset` above.
  const careGoal = form.watch('careGoal');
  const allowsApps = careGoal?.includeApps === true;

  // Reads the form, not `careGoal`: on mount this runs right after the reset above, before the watch has caught up.
  useEffect(() => {
    if (form.getValues('careGoal')?.includeApps !== true && form.getValues('providerType')) {
      form.setValue('providerType', null);
    }
  }, [allowsApps]);

  function _onSubmit(formData: UnvalidatedSearchFormData) {
    trackSearch(formData);
    onSubmit({
      ...formData,
      providerName: formData.providerName?.trim() === '' ? null : formData.providerName?.trim() ?? null,
      gender: formData.gender === '' ? null : formData.gender,
      // `undefined` while the filter is hidden. Normalize it to the `null` the URL reads back as, or the URL
      // round trip looks like a new search and runs it again.
      providerType: formData.providerType || null,
      language: formData.language === '' ? null : formData.language,
      location: formData.location!,
      network: formData.network!,
    });
  }

  const fetchCareGoalsSelectOptions = useCallback((query: string) => getCareGoalsSelectOptions({ query }), []);

  return (
    <Form form={form} onSubmit={_onSubmit}>
      <Stack>
        <InputGroup>
          <InputGroup.Item>
            <AutocompleteInput
              aria-label={careGoalPlaceholder}
              name="careGoal"
              fetchOptions={fetchCareGoalsSelectOptions}
              inputIcon={<MedicalServicesOutlinedIcon />}
              optionIcon={<MedicalServicesOutlinedIcon />}
              label={careGoalPlaceholder}
              exampleText={careGoalExampleText}
              //TODO: improve custom validation across multiple inputs. https://app.asana.com/0/1203374383582495/1205127243912402/f
              customValidation={specialtyOrNameValidator(isNarrowView ? '' : specialtyOrNameValidationText)}
              debounceWait={200}
            />
          </InputGroup.Item>
          <InputGroup.Item>
            <TextInput
              name="providerName"
              label={providerNamePlaceholder}
              fullWidth
              startIcon={<AccountCircleOutlinedIcon />}
              customValidation={specialtyOrNameValidator(isNarrowView ? specialtyOrNameValidationText : '')}
              allowClear
            />
          </InputGroup.Item>

          <InputGroup.Item>
            <AutocompleteInput
              aria-label={locationPlaceholder}
              name="location"
              label={locationPlaceholder}
              exampleText={locationExampleText}
              fetchOptions={getGeocodeLocationSelectOptions}
              inputIcon={<LocationOnOutlinedIcon />}
              optionIcon={<LocationOnOutlinedIcon />}
              validation={{ required: true }}
              debounceWait={200}
            />
          </InputGroup.Item>

          <InputGroup.Item>
            <AutocompleteInput
              aria-label={networkPlaceholder}
              name="network"
              label={networkPlaceholder}
              exampleText={networkExampleText}
              fetchOptions={input => fetchNetworkData(networks, input)}
              inputIcon={<HubOutlinedIcon />}
              optionIcon={<HubOutlinedIcon />}
              debounceWait={0}
              validation={{ required: true }}
            />
          </InputGroup.Item>
        </InputGroup>

        <Grid spacing="sm">
          <Grid.Item xs={12} sm={4} lg={2}>
            <SelectInput
              name="language"
              label={languagePlaceholder}
              size="small"
              iconColor="common.muted"
              adornment={<LocationOnOutlinedIcon />}
              options={languageOptions}
            />
          </Grid.Item>
          <Grid.Item xs={12} sm={4} lg={2}>
            <SelectInput
              name="gender"
              label={genderPlaceholder}
              size="small"
              iconColor="common.muted"
              adornment={<PersonOutlineOutlinedIcon />}
              options={genderOptions}
            />
          </Grid.Item>
          {allowsApps ? (
            <Grid.Item xs={12} sm={4} lg={2}>
              <SelectInput
                name="providerType"
                label={providerTypePlaceholder}
                size="small"
                iconColor="common.muted"
                adornment={<BadgeOutlinedIcon />}
                options={providerTypeOptions}
              />
            </Grid.Item>
          ) : null}
          <Grid.Item xs={12} sm={allowsApps ? 12 : 4} lg={allowsApps ? 6 : 8} textAlign="right">
            <Button
              type="submit"
              variant="primary"
              size="small"
              paddingX="xl"
              icon={<SearchOutlinedIcon />}
              disabled={isDisabled}
            >
              <SR package="search" id="submitSearchButtonText" />
            </Button>
          </Grid.Item>
        </Grid>
      </Stack>
    </Form>
  );
};
